import React from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { setSnackbar } from 'flow/actions/app-actions';
import useAppContext from 'flow/hooks/useAppContext';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { prettyPersonalNumber } from 'flow/utils';
import { Agent } from 'flow/types/CallRecord';
import { TypedMap } from 'flow/types/TypedMap';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';

interface Props {
    agentInfo: TypedMap<Agent>;
}

const BANKID_CONFIG: { iconKey: IconKey; label: string } = {
    iconKey: 'bankid',
    label: 'BankID',
};

const IDENTITY_CONFIG: Record<string, { iconKey: IconKey; label: string }> = {
    BANKID: BANKID_CONFIG,
    CGI: BANKID_CONFIG,
    CGI_GRP3: BANKID_CONFIG,
    QRONA: BANKID_CONFIG,
    GRANDID: BANKID_CONFIG,
    ID_KOLLEN_FREJA: { iconKey: 'frejaeid', label: 'Freja' },
    ID_KOLLEN_MITID: { iconKey: 'mitid', label: 'MitID' },
};

const getCopyIconComponent = (tooltipText: string) => (
    <div className="cursor-pointer">
        <Tooltip label={tooltipText}>
            <div>
                <Icon iconKey={'copy'} className="size-5 text-gray-500" />
            </div>
        </Tooltip>
    </div>
);

const EidWidget = ({ agentInfo }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const copyToClipboard = () => {
        const copyToClipboardString =
            agentInfo.get('copyFormat') === 'NONE'
                ? agentInfo.get('personalNumber')
                : prettyPersonalNumber(agentInfo.get('personalNumber'));

        navigator.clipboard.writeText(copyToClipboardString);
        dispatch(
            setSnackbar({
                message: i18next.t('snackbar.copied_to_clipboard'),
            }),
        );
    };

    const identityType = agentInfo.get('identityType');
    const identityConfig = IDENTITY_CONFIG[identityType];
    return (
        <div
            className="flex cursor-pointer flex-col overflow-hidden rounded-md bg-gray-800"
            onClick={copyToClipboard}
        >
            {agentInfo.get('status') === 'SUCCESS' ? (
                <>
                    <div className="flex items-center justify-between bg-gray-800 p-3 text-white">
                        <div className="flex items-center gap-2">
                            {identityConfig && (
                                <>
                                    <Icon
                                        iconKey={identityConfig.iconKey}
                                        className="size-5"
                                    />
                                    <div className="text-xs font-light text-gray-400">
                                        {identityConfig.label}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex items-center">
                            <div className="text-green-500">
                                {i18next.t('verified_caller.verified')}
                            </div>
                            <Icon
                                iconKey={'done'}
                                className="me-2 size-5 text-green-500"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between bg-gray-900 p-3 text-white">
                        <div className="flex items-center gap-2">
                            <div className="text-gray-400">
                                {i18next.t('verified_caller.personal_number')}
                            </div>
                            <div>
                                {prettyPersonalNumber(
                                    agentInfo.get('personalNumber'),
                                )}
                            </div>
                        </div>
                        {getCopyIconComponent(i18next.t('Tooltip.Title.Copy'))}
                    </div>
                </>
            ) : (
                <>
                    <div className="flex items-center justify-between bg-gray-800 p-3 text-white">
                        <div className="flex items-center">
                            <Icon
                                iconKey={'info'}
                                className="size-5 text-gray-500"
                            />
                            <div className="text-gray-400">
                                {i18next.t(
                                    'Call.Canvas.Title.Identity_not_verified',
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between bg-gray-900 p-3 text-white">
                        <div className="flex items-center">
                            <div>
                                {prettyPersonalNumber(
                                    agentInfo.get('personalNumber'),
                                )}
                            </div>
                        </div>
                        {getCopyIconComponent(i18next.t('Tooltip.Title.Copy'))}
                    </div>
                </>
            )}
        </div>
    );
};

export default React.memo(EidWidget);
